import { useState } from 'react';
import { Link } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { useAssets } from '@/hooks/useAssets';
import { formatRelativeDate } from '@/utils/date';
import type { Asset } from '@/types/api';
import { AssetBinaryImage } from '@/components/domain/AssetBinaryPreview';
import { Icon } from '@/components/primitives/Icon';
import { Button } from '@/components/primitives/Button';
import { IconButton } from '@/components/primitives/IconButton';
import { Chip } from '@/components/primitives/Chip';
import { Skeleton } from '@/components/primitives/Skeleton';
import { UploadAssetModal } from '@/components/modals/UploadAssetModal';
import { downloadAssetContentFile, uploadAssetWithProgress } from '@/services/assets';

type FilterTab = 'All Assets' | 'Images' | 'Videos';
type ViewMode = 'list' | 'grid';

const FILTER_TABS: FilterTab[] = ['All Assets', 'Images', 'Videos'];

function assetMimeType(asset: Asset): string {
  const ext = asset.name.split('.').pop()?.toUpperCase() ?? '';
  return ext || asset.asset_type;
}

function assetCategory(asset: Asset): 'Image' | 'Video' {
  return asset.asset_type === 'video' ? 'Video' : 'Image';
}

function AssetThumb({ asset }: { asset: Asset }) {
  const endpoint = asset.thumbnail_url ? 'thumbnail' : 'content';
  const hint = asset.thumbnail_url ?? asset.uri;

  if (asset.asset_type === 'video') {
    return (
      <div className="aspectly:w-12 aspectly:h-12 aspectly:shrink-0 aspectly:bg-stone-900 aspectly:rounded-lg aspectly:overflow-hidden aspectly:relative aspectly:flex aspectly:items-center aspectly:justify-center">
        {asset.thumbnail_url && asset.id != null && (
          <AssetBinaryImage
            assetId={asset.id}
            endpoint="thumbnail"
            remoteUrlHint={asset.thumbnail_url}
            alt={asset.alt_text || asset.name}
            className="aspectly:w-full aspectly:h-full aspectly:object-cover aspectly:opacity-60"
            loadingClassName="aspectly:w-full aspectly:h-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-stone-900"
          />
        )}
        <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:items-center aspectly:justify-center">
          <Icon name="play_arrow" filled className="aspectly:text-white aspectly:text-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="aspectly:w-12 aspectly:h-12 aspectly:shrink-0 aspectly:bg-surface-container-low aspectly:rounded-lg aspectly:overflow-hidden">
      {asset.id != null ? (
        <AssetBinaryImage
          assetId={asset.id}
          endpoint={endpoint}
          remoteUrlHint={hint}
          alt={asset.alt_text || asset.name}
          className="aspectly:w-full aspectly:h-full aspectly:object-cover"
          loadingClassName="aspectly:w-full aspectly:h-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface-container-low"
        />
      ) : (
        <div className="aspectly:w-full aspectly:h-full aspectly:flex aspectly:items-center aspectly:justify-center">
          <Icon name="image" className="aspectly:text-on-surface-variant/40" />
        </div>
      )}
    </div>
  );
}

function GridThumb({ asset }: { asset: Asset }) {
  const orgId = useOrgId();
  const endpoint = asset.thumbnail_url ? 'thumbnail' : 'content';
  const hint = asset.thumbnail_url ?? asset.uri;

  const card = (
    <div className="aspectly:group aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:border aspectly:border-outline-variant/10 aspectly:overflow-hidden aspectly:hover:shadow-md aspectly:transition-all">
      <div className="aspectly:h-32 aspectly:relative aspectly:bg-surface-container-low aspectly:flex aspectly:items-center aspectly:justify-center aspectly:overflow-hidden">
        {asset.asset_type === 'video' && !asset.thumbnail_url ? (
          <Icon name="videocam" className="aspectly:text-on-surface-variant/30 aspectly:text-4xl" />
        ) : asset.id != null ? (
          <AssetBinaryImage
            assetId={asset.id}
            endpoint={endpoint}
            remoteUrlHint={hint}
            alt={asset.alt_text || asset.name}
            className="aspectly:w-full aspectly:h-full aspectly:object-cover"
            loadingClassName="aspectly:w-full aspectly:h-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface-container-low"
          />
        ) : (
          <Icon
            name={asset.asset_type === 'video' ? 'videocam' : 'image'}
            className="aspectly:text-on-surface-variant/30 aspectly:text-4xl"
          />
        )}
        {asset.asset_type === 'video' && (
          <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-black/30">
            <Icon name="play_circle" filled className="aspectly:text-white aspectly:text-4xl" />
          </div>
        )}
      </div>
      <div className="aspectly:p-3">
        <h4 className="aspectly:text-xs aspectly:font-bold aspectly:text-on-surface aspectly:truncate">{asset.name}</h4>
        <p className="aspectly:text-[10px] aspectly:text-on-surface-variant aspectly:mt-0.5">{assetMimeType(asset)}</p>
      </div>
    </div>
  );

  if (asset.id == null) {
    return card;
  }

  return (
    <Link to={orgUiPath(orgId, `/assets/${asset.id}`)} className="aspectly:block">
      {card}
    </Link>
  );
}

export default function AssetsPage() {
  const orgId = useOrgId();
  const [activeFilter, setActiveFilter] = useState<FilterTab>('All Assets');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState('');

  const { data, loading, error, prependAsset } = useAssets();

  const filtered = (data ?? []).filter((a) => {
    if (activeFilter === 'All Assets') return true;
    if (activeFilter === 'Images') return a.asset_type === 'image';
    if (activeFilter === 'Videos') return a.asset_type === 'video';
    return true;
  });

  async function handleAssetUpload(file: File, name: string) {
    setIsUploadModalOpen(false);
    setUploadError('');
    setUploadProgress(0);
    try {
      const created = await uploadAssetWithProgress({ file, name }, (pct) => {
        setUploadProgress(pct);
      });
      prependAsset(created);
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadProgress(null);
    }
  }

  async function handleAssetDownload(asset: Asset) {
    if (asset.id == null) return;
    setUploadError('');
    try {
      await downloadAssetContentFile(asset.id, asset.name);
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Download failed');
    }
  }

  return (
    <div className="aspectly:p-4 aspectly:md:p-8 aspectly:lg:p-12 aspectly:space-y-10">
      {/* Header */}
      <div className="aspectly:flex aspectly:items-end aspectly:justify-between aspectly:gap-6 aspectly:flex-wrap">
        <div>
          <h1 className="aspectly:text-4xl aspectly:font-black aspectly:font-headline aspectly:tracking-tight aspectly:text-on-surface">
            Asset Library
          </h1>
          <p className="aspectly:text-on-surface-variant aspectly:mt-1">
            Manage and organise your digital production assets.
          </p>
        </div>
        <div className="aspectly:flex aspectly:items-center aspectly:gap-4">
          {/* Progress Circular Upload Bar */}
          {uploadProgress !== null && (
            <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:bg-surface-container-low aspectly:px-3 aspectly:py-1.5 aspectly:rounded-full aspectly:border aspectly:border-outline-variant/10">
              <div className="aspectly:relative aspectly:flex aspectly:items-center aspectly:justify-center aspectly:w-8 aspectly:h-8">
                <svg className="aspectly:w-full aspectly:h-full aspectly:-rotate-90" viewBox="0 0 36 36">
                  <circle
                    className="aspectly:stroke-surface-container-highest"
                    cx="18"
                    cy="18"
                    fill="none"
                    r="16"
                    strokeWidth="3"
                  />
                  <circle
                    className="aspectly:stroke-primary aspectly:transition-all aspectly:duration-300 aspectly:ease-out"
                    cx="18"
                    cy="18"
                    fill="none"
                    r="16"
                    strokeWidth="3"
                    strokeDasharray="100"
                    strokeDashoffset={100 - uploadProgress}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="aspectly:absolute aspectly:text-[9px] aspectly:font-black aspectly:text-on-surface aspectly:tabular-nums aspectly:tracking-tighter">
                  {uploadProgress}%
                </span>
              </div>
            </div>
          )}

          {/* Upload button */}
          <Button onClick={() => setIsUploadModalOpen(true)} disabled={uploadProgress !== null}>
            <Icon name="add" className="aspectly:text-lg" />
            {uploadProgress !== null ? 'Uploading...' : 'Asset'}
          </Button>
        </div>
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
          {uploadError}
        </div>
      )}

      {/* Fetch error */}
      {error && (
        <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
          {error}
        </div>
      )}

      {/* Filter bar */}
      <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:py-4 aspectly:border-y aspectly:border-outline-variant/10">
        <div className="aspectly:flex aspectly:items-center aspectly:gap-2">
          {FILTER_TABS.map((tab) => (
            <Chip key={tab} active={activeFilter === tab} onClick={() => setActiveFilter(tab)}>
              {tab}
            </Chip>
          ))}
          {data && (
            <span className="aspectly:ml-2 aspectly:text-xs aspectly:text-on-surface-variant">
              {filtered.length} asset{filtered.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* View toggle */}
        <div className="aspectly:flex aspectly:bg-surface-container-low aspectly:p-1 aspectly:rounded-lg">
          <IconButton
            icon="grid_view"
            size="sm"
            onClick={() => setViewMode('grid')}
            className={
              viewMode === 'grid'
                ? 'aspectly:bg-surface-container-lowest aspectly:shadow-xs aspectly:text-primary' : 'aspectly:text-on-surface-variant aspectly:hover:text-on-surface'
            }
          />
          <IconButton
            icon="list"
            size="sm"
            onClick={() => setViewMode('list')}
            className={
              viewMode === 'list'
                ? 'aspectly:bg-surface-container-lowest aspectly:shadow-xs aspectly:text-primary' : 'aspectly:text-on-surface-variant aspectly:hover:text-on-surface'
            }
          />
        </div>
      </div>

      {/* Loading skeletons */}
      {loading && (
        <div className="aspectly:space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} rounded="xl" className="aspectly:h-16" />
          ))}
        </div>
      )}

      {/* List view */}
      {!loading && viewMode === 'list' && (
        <div className="aspectly:overflow-x-auto aspectly:-mx-4 aspectly:px-4 aspectly:md:mx-0 aspectly:md:px-0">
          <div className="aspectly:space-y-1 aspectly:min-w-[700px]">
            <div className="aspectly:grid aspectly:grid-cols-[1fr_100px_80px_80px_120px_48px] aspectly:gap-4 aspectly:px-6 aspectly:py-3 aspectly:text-[10px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:text-on-surface-variant/60">
              <div>Name</div>
              <div>Category</div>
              <div>Type</div>
              <div>Modified</div>
              <div className="aspectly:text-right">Actions</div>
              <div />
            </div>
            {filtered.map((asset) => (
              <div
                key={asset.id}
                className="aspectly:group aspectly:grid aspectly:grid-cols-[1fr_100px_80px_80px_120px_48px] aspectly:gap-4 aspectly:items-center aspectly:px-6 aspectly:py-4 aspectly:bg-surface-container-lowest aspectly:border aspectly:border-outline-variant/10 aspectly:rounded-xl aspectly:hover:shadow-md aspectly:transition-all aspectly:mb-2"
              >
                <div className="aspectly:flex aspectly:items-center aspectly:gap-4 aspectly:overflow-hidden">
                  <AssetThumb asset={asset} />
                  <div className="aspectly:min-w-0">
                    <h4 className="aspectly:text-sm aspectly:font-bold aspectly:text-on-surface aspectly:truncate">{asset.name}</h4>
                    <p className="aspectly:text-[10px] aspectly:text-on-surface-variant">{asset.alt_text || '—'}</p>
                  </div>
                </div>
                <div className="aspectly:text-xs aspectly:font-medium aspectly:text-on-surface-variant">
                  {assetCategory(asset)}
                </div>
                <div className="aspectly:text-xs aspectly:font-medium aspectly:text-on-surface-variant">
                  {assetMimeType(asset)}
                </div>
                <div className="aspectly:text-xs aspectly:font-medium aspectly:text-on-surface-variant">
                  {formatRelativeDate(asset.updated_at)}
                </div>
                <div className="aspectly:flex aspectly:items-center aspectly:justify-end aspectly:gap-3">
                  {asset.id != null ? (
                    <Link
                      to={orgUiPath(orgId, `/assets/${asset.id}`)}
                      className="aspectly:text-xs aspectly:font-bold aspectly:text-primary aspectly:hover:underline aspectly:cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Preview
                    </Link>
                  ) : (
                    <span className="aspectly:text-xs aspectly:font-bold aspectly:text-on-surface-variant/60">Preview</span>
                  )}
                  <button
                    type="button"
                    className="aspectly:w-8 aspectly:h-8 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:rounded-full aspectly:border aspectly:border-outline-variant/30 aspectly:text-on-surface-variant aspectly:hover:bg-primary/5 aspectly:hover:text-primary aspectly:transition-colors aspectly:cursor-pointer aspectly:disabled:opacity-40 aspectly:disabled:cursor-not-allowed"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleAssetDownload(asset);
                    }}
                    disabled={asset.id == null}
                  >
                    <Icon name="download" className="aspectly:text-lg" />
                  </button>
                </div>
                <IconButton icon="more_vert" size="lg" className="aspectly:hover:bg-surface-container" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid view */}
      {!loading && viewMode === 'grid' && (
        <div className="aspectly:grid aspectly:grid-cols-2 aspectly:md:grid-cols-3 aspectly:lg:grid-cols-5 aspectly:gap-4">
          {filtered.map((asset) => (
            <GridThumb key={asset.id} asset={asset} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && !error && (
        <div className="aspectly:py-24 aspectly:text-center">
          <Icon name="folder_open" className="aspectly:text-6xl aspectly:text-primary/30" />
          <p className="aspectly:mt-4 aspectly:text-on-surface-variant aspectly:font-medium">No assets yet.</p>
          <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:mt-1">
            Upload logos, images, or videos to get started.
          </p>
        </div>
      )}

      <UploadAssetModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onConfirm={handleAssetUpload}
      />
    </div>
  );
}
